import type { Summary } from './api.ts';
import { setThemeColour, token } from './theme.ts';

/**
 * Laat de paginakleur het weer volgen.
 *
 * De tint komt van de temperatuur en de helderheid van de zonstand: 's nachts
 * dieper, rond het middaguur lichter. Zo klopt de pagina met wat er buiten
 * gebeurt zonder dat er een tweede getal bij hoeft.
 *
 * Navy houdt de tint kort: van 262° bij vorst naar 240° in de hitte — allebei
 * blauw, het ene koeler dan het andere. De vorige stijl liep over 209° van blauw
 * naar roze, wat werkte omdat de kaarten er als wit papier bovenop lagen. Hier
 * ís de achtergrond de pagina; wie daar de hele kleurencirkel op loslaat, zet de
 * tekst om vier uur 's middags op een ander vlak dan om acht uur 's avonds.
 *
 * Gerekend in oklch omdat die ruimte waarneembaar uniform is: gelijke stappen in
 * L zien er ook echt even groot uit, wat in hsl niet opgaat. Neergezet als
 * sRGB-hex — zie `srgbHex` hieronder voor waarom niet als `oklch(…)`.
 *
 * Eén vlakke kleur, geen verloop. Zonder kaarten loopt de pagina door tot in de
 * voettekst, en een verticaal verloop over die hele hoogte laat de secties
 * onderaan in een andere kleur staan dan bovenaan.
 */
export function applyWeatherBackground(summary: Summary, enabled: boolean): void {
  const body = document.body;
  const root = document.documentElement;

  /*
   * `--ground` is de kleur van het vlak waar alles op ligt. Zolang de
   * achtergrond niet meekleurt is dat gewoon `--page` uit styles.css; zodra ze
   * dat wel doet, moet elke stip die een lijn doorbreekt en elke plakkende
   * tabelkop exact déze kleur krijgen. Vandaar dat hij hier wordt bijgeschreven
   * en niet in de CSS blijft staan.
   */
  const plain = () => {
    body.style.background = '';
    root.style.removeProperty('--ground');
    // De balk boven de pagina loopt door in de pagina; die moet dus mee.
    setThemeColour(token('--page'));
  };

  if (!enabled) {
    plain();
    return;
  }

  const temperature = summary.now.temperature ?? summary.today.max;
  if (temperature === null) {
    plain();
    return;
  }

  const now = new Date();
  const hour = now.getHours() + now.getMinutes() / 60;

  const asHours = (ts: number | null): number | null => {
    if (ts === null) return null;
    const d = new Date(ts * 1000);
    return d.getHours() + d.getMinutes() / 60;
  };
  const sunrise = asHours(summary.sun.sunrise) ?? 7;
  const sunset = asHours(summary.sun.sunset) ?? 21;

  // 0 in de nacht, 1 rond het hoogste punt van de zon.
  const day =
    hour <= sunrise || hour >= sunset
      ? 0
      : Math.sin((Math.PI * (hour - sunrise)) / Math.max(0.1, sunset - sunrise));

  // −5 °C → 262° (koel blauw), 40 °C → 240° (warmer blauw).
  const warm = Math.min(1, Math.max(0, (temperature + 5) / 45));
  const hue = 262 - warm * 22;

  /*
   * Donker is de hoofdstand van Navy: L loopt van 0,21 in de nacht naar 0,28
   * rond het middaguur, met chroma van 0,055 naar 0,085. Dat is precies de band
   * waar `--page` #081c3f in valt, zodat het omzetten van de schakelaar geen
   * sprong geeft.
   *
   * Licht doet hetzelfde omgekeerd en veel zachter: van 0,97 naar 0,94. De
   * pagina is daar het lichtste vlak, dus meer beweging dan dat zou de tekst
   * eronder laten zwemmen.
   */
  const dark = document.documentElement.dataset.theme === 'dark';
  const L = dark ? 0.21 + 0.07 * day : 0.97 - 0.03 * day;
  const C = dark ? 0.055 + 0.03 * day : 0.012 + 0.01 * day;

  const colour = srgbHex(L, C, hue);
  body.style.background = colour;
  root.style.setProperty('--ground', colour);
  setThemeColour(colour);
}

/**
 * oklch → sRGB-hex, afgekapt op de rand van het gamma.
 *
 * De kleur wórdt in oklch gerekend, maar er gaat een hex de pagina in. Dat is
 * geen omweg maar de enige waarde die overal aankomt: de `theme-color`-metatag
 * en de canvas van Chart.js parsen allebei zelf een kleur, en een `oklch(…)` die
 * de browser niet kent wordt daar niet benaderd maar genegeerd. De metatag valt
 * dan terug op het wit van de browser — een lichte balk boven een donkere
 * pagina — en de stippen die een lijn moeten doorbreken houden de kleur die er
 * al stond. Een hex kent elke browser, en de pagina, de balk en de stippen
 * staan gegarandeerd op exact dezelfde kleur.
 *
 * Afkappen, niet terugvouwen. Aan het warme eind van de donkere modus duikt het
 * rode kanaal net onder nul — een browser zou daar het chroma terugdraaien tot
 * de kleur weer past en komt zo op een haartje verschil uit (#002c45 waar wij
 * #002c4e zetten, in het uiterste geval; één kanaal, minder dan 4 %). Het
 * eenvoudige afkappen wint hier omdat palette-check.mjs dezelfde stap zet: de
 * contrastgetallen die dat script afdrukt gelden dan voor de kleur die er
 * werkelijk ligt, in plaats van voor een kleur die er ongeveer op lijkt.
 *
 * LET OP: dezelfde formules staan in web/tools/palette-check.mjs, dat het
 * contrast op deze vlakken narekent. Ze horen samen te veranderen.
 */
function srgbHex(L: number, C: number, hueDeg: number): string {
  const h = (hueDeg * Math.PI) / 180;
  const a = C * Math.cos(h);
  const b = C * Math.sin(h);
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;

  const toGamma = (c: number) => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);
  const channels = [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];

  return `#${channels
    .map((c) =>
      Math.round(Math.min(1, Math.max(0, toGamma(c))) * 255)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}
